import { createSignal, createResource, For, Show, onMount } from "solid-js";
import { useApi, isOk } from "../api";
import { Pagination } from "../components/Pagination";
import { DateRangePills, type DateRangeValue } from "../components/DateRangePills";
import Text from "../components/Text";
import ItemStackView from "../components/ItemStackView";
import { fromItemDef } from "../services/itemRefAdapters";
import { useToast } from "../components/MessageBox";
import { useItemName } from '../hooks/useItemName';
import { useTranslation } from "../hooks/useTranslation";
import { SkeletonAuditRow } from "../components/Skeleton";
import QuantityInput from "../components/QuantityInput";
import { formatQuantity } from "../services/quantityFormatter";
import {
  useInventoryApi,
  type StorageInfo,
  type AuditLogItem,
} from "../services/inventoryApi";
import { useCraftingResolver } from "../services/craftingResolverContext";
import { formatDateTime } from "../services/dateFormat";
import StorageList from "../components/inventory/StorageList";
import styles from "./InventoryAuditLog.module.css";

function badgeClass(actionType: string): string {
  switch (actionType) {
    case "CraftCompleted": return styles.badgeCraft;
    case "TransferCompleted": return styles.badgeTransfer;
    case "ManualInput": return styles.badgeManualInput;
    case "ManualExtraction": return styles.badgeManualExtraction;
    case "StorageItemsSet": return styles.badgeSet;
    case "ScanStarted": return styles.badgeScanStarted;
    case "ScanCompleted": return styles.badgeScanCompleted;
    case "ScanFailed": return styles.badgeScanFailed;
    case "ChestContentsRead": return styles.badgeChestRead;
    case "ChestConfigUpdated": return styles.badgeChestConfig;
    case "ChestsReset": return styles.badgeChestsReset;
    default: return "";
  }
}

function badgeIcon(actionType: string): string {
  switch (actionType) {
    case "CraftCompleted": return "fa-solid fa-hammer";
    case "TransferCompleted": return "fa-solid fa-right-left";
    case "ManualInput": return "fa-solid fa-arrow-down";
    case "ManualExtraction": return "fa-solid fa-arrow-up";
    case "StorageItemsSet": return "fa-solid fa-rotate";
    case "ScanStarted": return "fa-solid fa-radar";
    case "ScanCompleted": return "fa-solid fa-circle-check";
    case "ScanFailed": return "fa-solid fa-circle-xmark";
    case "ChestContentsRead": return "fa-solid fa-box-open";
    case "ChestConfigUpdated": return "fa-solid fa-sliders";
    case "ChestsReset": return "fa-solid fa-trash";
    default: return "fa-solid fa-circle";
  }
}

function actionLabelKey(actionType: string): string {
  switch (actionType) {
    case "CraftCompleted": return "AUDIT_CRAFT";
    case "TransferCompleted": return "AUDIT_TRANSFER";
    case "ManualInput": return "AUDIT_MANUAL_INPUT";
    case "ManualExtraction": return "AUDIT_MANUAL_EXTRACTION";
    case "StorageItemsSet": return "AUDIT_SYNC";
    case "ScanStarted": return "AUDIT_SCAN_STARTED";
    case "ScanCompleted": return "AUDIT_SCAN_COMPLETED";
    case "ScanFailed": return "AUDIT_SCAN_FAILED";
    case "ChestContentsRead": return "AUDIT_CHEST_READ";
    case "ChestConfigUpdated": return "AUDIT_CHEST_CONFIG";
    case "ChestsReset": return "AUDIT_CHESTS_RESET";
    default: return "AUDIT_SYNC";
  }
}

function parseItems(json: string): { removed: AuditLogItem[]; added: AuditLogItem[] } {
  try {
    const parsed = JSON.parse(json);
    return { removed: parsed.removed ?? [], added: parsed.added ?? [] };
  } catch {
    return { removed: [], added: [] };
  }
}

export default function InventoryAuditLog() {
  const toast = useToast();
  const { t } = useTranslation();
  const { itemName } = useItemName();
  const api = useApi();
  const inv = useInventoryApi();
  const resolver = useCraftingResolver();
  const itemInfo = (key: string) => resolver()?.getItem(key);

  const [storages, setStorages] = createSignal<StorageInfo[]>([]);
  const [selectedStorage, setSelectedStorage] = createSignal<string>("");
  const [actionFilter, setActionFilter] = createSignal("");
  const [userFilter, setUserFilter] = createSignal("");
  const [quickFilter, setQuickFilter] = createSignal<DateRangeValue>("all");
  const [page, setPage] = createSignal(0);
  const pageSize = 30;

  const [accessUsers, setAccessUsers] = createSignal<{ userId: string; username: string }[]>([]);

  const [showManualForm, setShowManualForm] = createSignal(false);
  const [manualType, setManualType] = createSignal<"input" | "extraction">("extraction");
  const [manualItems, setManualItems] = createSignal<Record<string, number>>({});
  const [manualItemSearch, setManualItemSearch] = createSignal("");
  const [manualNote, setManualNote] = createSignal("");
  const [noteError, setNoteError] = createSignal(false);
  const [stagedItemKey, setStagedItemKey] = createSignal<string | null>(null);
  const [stagedQty, setStagedQty] = createSignal(1);

  async function loadAccessUsers(storageId: string) {
    try {
      const res = await api.access.getAssignments(storageId);
      if (!isOk(res.data)) { setAccessUsers([]); return; }
      const access = res.data.value as any;
      const users = (access.assignments ?? [])
        .filter((a: any) => a.principalType === 0)
        .map((a: any) => ({ userId: a.principalId, username: a.principalName }));
      if (access.ownerName) users.unshift({ userId: access.ownerId, username: access.ownerName });
      setAccessUsers(users);
    } catch { setAccessUsers([]); }
  }

  onMount(async () => {
    const s = await inv.fetchStorages();
    const adminStorages = s.filter(st => st.myActions.includes("storage.audit.read"));
    setStorages(adminStorages);
    if (adminStorages.length > 0) {
      setSelectedStorage(adminStorages[0].id);
      loadAccessUsers(adminStorages[0].id);
    }
  });

  function selectStorage(id: string) {
    setSelectedStorage(id);
    setPage(0);
    setUserFilter("");
    loadAccessUsers(id);
  }

  function getDateRange(): { from?: string; to?: string } {
    const now = new Date();
    switch (quickFilter()) {
      case "today": {
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return { from: start.toISOString() };
      }
      case "7d": {
        const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return { from: start.toISOString() };
      }
      case "30d": {
        const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return { from: start.toISOString() };
      }
      default: return {};
    }
  }

  const [logData, { refetch }] = createResource(
    () => ({
      storageId: selectedStorage(),
      actionType: actionFilter(),
      userId: userFilter(),
      quickFilter: quickFilter(),
      page: page(),
    }),
    async (params) => {
      if (!params.storageId) return { items: [], total: 0 };
      const range = getDateRange();
      return inv.fetchAuditLog(params.storageId, {
        actionType: params.actionType || undefined,
        from: range.from,
        to: range.to,
        filterUserId: params.userId || undefined,
        page: params.page,
        pageSize,
      });
    }
  );

  const totalPages = () => Math.ceil((logData()?.total ?? 0) / pageSize);

  function searchMatchingItems() {
    const q = manualItemSearch().toLowerCase().trim();
    const res = resolver();
    if (!q || q.length < 2 || !res) return [];
    return res.getAllItems()
      .filter(i => res.matchesSearch(i.key, q))
      .slice(0, 8);
  }

  function handleItemSelect(key: string) {
    setStagedItemKey(key);
    setStagedQty(1);
    setManualItemSearch("");
  }

  function confirmStagedItem() {
    const key = stagedItemKey();
    if (!key || stagedQty() < 1) return;
    const current = { ...manualItems() };
    current[key] = (current[key] ?? 0) + stagedQty();
    setManualItems(current);
    setStagedItemKey(null);
    setStagedQty(1);
  }

  function cancelStagedItem() {
    setStagedItemKey(null);
    setStagedQty(1);
  }

  function removeManualItem(key: string) {
    const current = { ...manualItems() };
    delete current[key];
    setManualItems(current);
  }

  async function submitManualEntry() {
    const items = manualItems();
    if (Object.keys(items).length === 0) {
      toast?.error(t("AUDIT_ADD_ITEM"));
      return;
    }
    if (!manualNote().trim()) {
      setNoteError(true);
      toast?.error(t("AUDIT_NOTE_REQUIRED"));
      return;
    }
    setNoteError(false);
    try {
      await inv.createManualAuditEntry(selectedStorage(), manualType(), items, manualNote());
      toast?.info(t("AUDIT_RECORDED"));
      setShowManualForm(false);
      setManualItems({});
      setManualNote("");
      setManualItemSearch("");
      refetch();
    } catch (e: any) {
      toast?.error(e?.message ?? t("AUDIT_RECORD_FAILED"));
    }
  }

  return (
    <div class={styles.layout}>
      <StorageList storages={storages} selectedId={selectedStorage} onSelect={selectStorage} />

      <div class={styles.container}>
      <div class={styles.headerRow}>
        <h1><Text val="INVENTORY_AUDIT_LOG" /></h1>
        <Show when={logData()?.total}>
          <span class={styles.entryCount}>{logData()!.total} <Text val="AUDIT_ENTRIES" /></span>
        </Show>
      </div>

      <div class={styles.filters}>
        <select value={actionFilter()} onChange={(e) => { setActionFilter(e.currentTarget.value); setPage(0); }}>
          <option value=""><Text val="AUDIT_ALL_ACTIONS" /></option>
          <option value="ManualInput"><Text val="AUDIT_MANUAL_INPUT" /></option>
          <option value="ManualExtraction"><Text val="AUDIT_MANUAL_EXTRACTION" /></option>
          <option value="CraftCompleted"><Text val="AUDIT_CRAFT" /></option>
          <option value="TransferCompleted"><Text val="AUDIT_TRANSFER" /></option>
          <option value="StorageItemsSet"><Text val="AUDIT_SYNC" /></option>
          <option value="ScanStarted"><Text val="AUDIT_SCAN_STARTED" /></option>
          <option value="ScanCompleted"><Text val="AUDIT_SCAN_COMPLETED" /></option>
          <option value="ScanFailed"><Text val="AUDIT_SCAN_FAILED" /></option>
          <option value="ChestContentsRead"><Text val="AUDIT_CHEST_READ" /></option>
          <option value="ChestConfigUpdated"><Text val="AUDIT_CHEST_CONFIG" /></option>
          <option value="ChestsReset"><Text val="AUDIT_CHESTS_RESET" /></option>
        </select>

        <select value={userFilter()} onChange={(e) => { setUserFilter(e.currentTarget.value); setPage(0); }}>
          <option value=""><Text val="AUDIT_ALL_USERS" /></option>
          <For each={accessUsers()}>
            {(u) => <option value={u.userId}>{u.username}</option>}
          </For>
        </select>

        <DateRangePills
          value={quickFilter()}
          onChange={(v) => { setQuickFilter(v); setPage(0); }}
          pills={[
            { value: 'all',   labelKey: 'AUDIT_ALL_TIME' },
            { value: 'today', labelKey: 'AUDIT_TODAY' },
            { value: '7d',    labelKey: 'AUDIT_LAST_7_DAYS' },
            { value: '30d',   labelKey: 'AUDIT_LAST_30_DAYS' },
          ]}
        />

        <button class={styles.recordBtn} onClick={() => setShowManualForm(!showManualForm())}>
          <i class="fa-solid fa-pen-to-square"></i>
          <Text val="AUDIT_RECORD_ACTION" />
        </button>
      </div>

      <Show when={showManualForm()}>
        <div class={styles.manualForm}>
          <h2><Text val="AUDIT_RECORD_TITLE" /></h2>

          <div class={styles.typeToggle}>
            <button class={manualType() === "extraction" ? styles.typeActive : ""} onClick={() => setManualType("extraction")}>
              <i class="fa-solid fa-arrow-right-from-bracket"></i> <Text val="AUDIT_MANUAL_EXTRACTION" />
            </button>
            <button class={manualType() === "input" ? styles.typeActive : ""} onClick={() => setManualType("input")}>
              <i class="fa-solid fa-arrow-right-to-bracket"></i> <Text val="AUDIT_MANUAL_INPUT" />
            </button>
          </div>

          <Show when={!stagedItemKey()} fallback={
            (() => {
              const staged = () => itemInfo(stagedItemKey()!);
              return (
                <div class={styles.stagedItem}>
                  <div class={styles.stagedItemHeader}>
                    <Show when={staged()}>
                      {(it) => <ItemStackView stack={fromItemDef(it())} size={32} />}
                    </Show>
                    <span class={styles.stagedItemName}>{staged() ? itemName(stagedItemKey()!) : stagedItemKey()}</span>
                    <button class={styles.stagedCancel} onClick={cancelStagedItem} aria-label={t('AUDIT_CANCEL_STAGED_ITEM')}><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
                  </div>
                  <QuantityInput value={stagedQty()} onChange={setStagedQty} stackSize={staged()?.stackSize} />
                  <button class="btn btn-primary" onClick={confirmStagedItem} disabled={stagedQty() < 1}>
                    <i class="fa-solid fa-plus"></i> <Text val="AUDIT_ADD_ITEM_BTN" />
                  </button>
                </div>
              );
            })()
          }>
            <div class={styles.searchWrap}>
              <input
                class={styles.searchInput}
                type="text"
                placeholder={t('AUDIT_SEARCH_ITEM_PLACEHOLDER')}
                value={manualItemSearch()}
                onInput={(e) => setManualItemSearch(e.currentTarget.value)}
              />
              <Show when={manualItemSearch().length >= 2 && searchMatchingItems().length > 0}>
                <div class={styles.searchDropdown}>
                  <For each={searchMatchingItems()}>
                    {(item) => (
                      <div class={styles.searchResult} onClick={() => handleItemSelect(item.key)}>
                        <ItemStackView stack={fromItemDef(item)} size={24} />
                        <span class={styles.searchResultName}>{item.displayName}</span>
                      </div>
                    )}
                  </For>
                </div>
              </Show>
            </div>
          </Show>

          <Show when={Object.keys(manualItems()).length > 0}>
            <div class={styles.selectedItems}>
              <For each={Object.entries(manualItems())}>
                {([key, qty]) => {
                  const item = itemInfo(key);
                  return (
                    <div class={styles.selectedItem}>
                      <Show when={item}>
                        {(it) => <ItemStackView stack={fromItemDef(it())} size={20} />}
                      </Show>
                      <span>{item ? itemName(key) : key} x{formatQuantity(qty, item?.stackSize)}</span>
                      <button onClick={() => removeManualItem(key)}>&times;</button>
                    </div>
                  );
                }}
              </For>
            </div>
          </Show>

          <textarea
            class={noteError() ? styles.noteRequired : ""}
            placeholder={t('AUDIT_REASON_PLACEHOLDER')}
            value={manualNote()}
            onInput={(e) => { setManualNote(e.currentTarget.value); setNoteError(false); }}
            rows={2}
          />

          <div class={styles.formActions}>
            <button onClick={() => { setShowManualForm(false); setManualItems({}); setManualNote(""); setManualItemSearch(""); }}>
              <Text val="AUDIT_CANCEL" />
            </button>
            <button
              class="btn btn-primary"
              onClick={submitManualEntry}
              disabled={Object.keys(manualItems()).length === 0}
            >
              <i class={`fa-solid ${manualType() === "extraction" ? "fa-arrow-right-from-bracket" : "fa-arrow-right-to-bracket"}`}></i>
              {manualType() === "extraction" ? <Text val="AUDIT_SUBMIT_EXTRACTION" /> : <Text val="AUDIT_SUBMIT_INPUT" />}
            </button>
          </div>
        </div>
      </Show>

      <Show when={!logData.loading} fallback={
        <div class={styles.logList}>
          <SkeletonAuditRow />
          <SkeletonAuditRow />
          <SkeletonAuditRow />
          <SkeletonAuditRow />
          <SkeletonAuditRow />
        </div>
      }>
        <Show when={(logData()?.items.length ?? 0) > 0} fallback={
          <div class={styles.empty}>
            <i class="fa-solid fa-clipboard-list" />
            <p><Text val="AUDIT_NO_ENTRIES" /></p>
          </div>
        }>
          <div class={styles.logList}>
            <For each={logData()!.items}>
              {(entry, idx) => {
                const items = () => parseItems(entry.itemsJson);
                return (
                  <div class={styles.logEntry} style={{ "animation-delay": `${idx() * 0.03}s` }}>
                    <div class={styles.logHeader}>
                      <span class={styles.logTime}>{formatDateTime(entry.createdAt)}</span>
                      <span class={`${styles.logBadge} ${badgeClass(entry.actionType)}`}>
                        <i class={badgeIcon(entry.actionType)}></i>
                        <Text val={actionLabelKey(entry.actionType)} />
                      </span>
                      <span class={styles.logUser}><i class="fa-solid fa-user" style={{ "margin-right": "4px", "font-size": "11px", opacity: "0.5" }}></i>{entry.username}</span>
                    </div>

                    <div class={styles.logStorages}>
                      <Show when={entry.sourceStorageName && entry.targetStorageName && entry.sourceStorageId !== entry.targetStorageId}>
                        <i class="fa-solid fa-warehouse"></i>
                        <strong>{entry.sourceStorageName}</strong>
                        <i class="fa-solid fa-arrow-right"></i>
                        <strong>{entry.targetStorageName}</strong>
                      </Show>
                      <Show when={entry.sourceStorageName && (!entry.targetStorageName || entry.sourceStorageId === entry.targetStorageId)}>
                        <i class="fa-solid fa-warehouse"></i>
                        <strong>{entry.sourceStorageName}</strong>
                      </Show>
                      <Show when={!entry.sourceStorageName && entry.targetStorageName}>
                        <i class="fa-solid fa-arrow-right"></i>
                        <i class="fa-solid fa-warehouse"></i>
                        <strong>{entry.targetStorageName}</strong>
                      </Show>
                    </div>

                    <div class={styles.itemRows}>
                      <For each={items().removed}>
                        {(item) => {
                          const info = () => itemInfo(item.itemKey);
                          return (
                            <div class={styles.itemRow}>
                              <Show when={info()}>
                                {(it) => <ItemStackView stack={fromItemDef(it())} size={22} />}
                              </Show>
                              <span class={`${styles.itemQty} ${styles.itemRemoved}`}>-{formatQuantity(item.quantity, info()?.stackSize)}</span>
                              <span class={styles.itemName}>{info() ? itemName(item.itemKey) : item.displayName ?? item.itemKey}</span>
                            </div>
                          );
                        }}
                      </For>
                      <For each={items().added}>
                        {(item) => {
                          const info = () => itemInfo(item.itemKey);
                          return (
                            <div class={styles.itemRow}>
                              <Show when={info()}>
                                {(it) => <ItemStackView stack={fromItemDef(it())} size={22} />}
                              </Show>
                              <span class={`${styles.itemQty} ${styles.itemAdded}`}>+{formatQuantity(item.quantity, info()?.stackSize)}</span>
                              <span class={styles.itemName}>{info() ? itemName(item.itemKey) : item.displayName ?? item.itemKey}</span>
                            </div>
                          );
                        }}
                      </For>
                    </div>

                    <Show when={entry.note}>
                      <div class={styles.logNote}>
                        <i class="fa-solid fa-quote-left" style={{ "margin-right": "6px", opacity: "0.4", "font-size": "10px" }}></i>
                        {entry.note}
                      </div>
                    </Show>
                  </div>
                );
              }}
            </For>
          </div>

          <Pagination page={page()} totalPages={totalPages()} onChange={setPage} />
        </Show>
      </Show>
      </div>
    </div>
  );
}
